import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { assignPaymentToPatient } from 'api/Billing';

import useModal from 'lib/hooks/useModal';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { rewritePortalPath } from 'lib/hooks/usePortalNavigate';

import Popover from 'components/shared/Popovers/Popover/Popover';
import AssignPatientModal from 'components/practice/shared/components/AssignPatientModal';

const InternalInvoicesNavigatePopover = ({ data }) => {
  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    isOpen: isPatientModalOpen,
    openModal: openPatientModal,
    closeModal: closePatientModal
  } = useModal();

  const { patient_id, appointment_id, eraIds = [] } = data || {};

  const { mutate: mutateAssignToPatient, isLoading } = useCustomMutation({
    mutationFn: (patientId) =>
      assignPaymentToPatient(navigate, { refId: data?.id, refType: 'invoice', patientId }),
    invalidateQueryKey: ['payments', 'internal_invoices'],
    successMessage: 'Invoice assigned to patient',
    errorMessage: "Couldn't assign invoice to patient",
    onSuccess: () => closePatientModal()
  });

  if (!data) return null;

  const options = [
    {
      label: 'Preview invoice',
      onClick: () => {
        const params = new URLSearchParams(search);
        params.set('preview', 'true');
        navigate(`${data.id}?${params.toString()}`);
      },
      icon: 'eye-mono'
    },
    {
      label: 'View patient',
      onClick: () =>
        window.open(rewritePortalPath(`/portal/charts/${patient_id}/billing/invoices`), '_blank'),
      icon: 'user-2-mono'
    },
    appointment_id && {
      label: 'View appointment',
      onClick: () =>
        window.open(
          rewritePortalPath(`/portal/appointments?appointment_id=${appointment_id}`),
          '_blank'
        ),
      icon: 'calendar-tick-mono'
    },
    eraIds?.length > 0 && {
      label: 'View ERA',
      icon: 'wallet-mono',
      children: eraIds.map((eraId) => ({
        label: eraId,
        onClick: () =>
          window.open(rewritePortalPath(`/portal/beyond-billing/era/${eraId}`), '_blank')
      }))
    },
    !patient_id && {
      label: 'Assign patient',
      onClick: openPatientModal,
      icon: 'user-circle-add-mono'
    }
  ].filter(Boolean);

  return (
    <>
      <Popover
        className="flex h-full w-full items-center justify-center"
        position="left"
        isFixed
        icon="context-menu-horizontal"
        options={options}
        isBorder={false}
      />

      <AssignPatientModal
        open={isPatientModalOpen}
        setOpen={closePatientModal}
        loading={isLoading}
        onAssign={(patient) => {
          if (!patient?.id) return;
          mutateAssignToPatient(patient?.id);
        }}
      />
    </>
  );
};

export default InternalInvoicesNavigatePopover;
